import { useCallback, useEffect, useMemo, useState } from 'react';

import type { IManagementReportDataset } from '@commenda/api';

import type { IReportContextValue } from '../context/ReportContext';
import { FactTableHelper } from '../helpers/FactTableHelper';
import { InlineOverrideHelper } from '../helpers/InlineOverrideHelper';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import { SavedViewStorageHelper } from '../helpers/SavedViewStorageHelper';
import type { BucketOverrides, ReportingBucket } from '../types/buckets';
import type { IPeriodFilter, MonthKey } from '../types/period';
import type { IReportItem, IReportSavedView } from '../types/report';
import { useSavedViews } from './useSavedViews';

export interface IUseReportStateInput {
  dataset: IManagementReportDataset;
  currency: string;
  booksClosedThrough: MonthKey;
  /** MANAGEMENT_REPORT_COLUMN_LAYOUT: column headers, breakdown lists and clean chart defaults. */
  columnLayout: boolean;
  /** MANAGEMENT_REPORT_INLINE_OVERRIDE: period override from the card menu instead of the dialog. */
  inlineOverride: boolean;
}

const stripLayoutNoise = (items: IReportItem[]) =>
  items.map(({ instanceId, componentId, layout, override }) => ({
    instanceId,
    componentId,
    layout,
    override: override ?? null,
  }));

const sameView = (
  a: { filter: IPeriodFilter; items: IReportItem[] },
  b: { filter: IPeriodFilter; items: IReportItem[] }
) =>
  JSON.stringify([a.filter, stripLayoutNoise(a.items)]) ===
  JSON.stringify([b.filter, stripLayoutNoise(b.items)]);

/** Owns filter, layout, bucket overrides and saved views for one report. */
export const useReportState = ({
  dataset,
  currency,
  booksClosedThrough,
  columnLayout,
  inlineOverride,
}: IUseReportStateInput): IReportContextValue => {
  const builtInViews = useMemo(
    () => ReportComponentHelper.builtInViews(booksClosedThrough, columnLayout),
    [booksClosedThrough, columnLayout]
  );
  const defaultView = builtInViews[0] as IReportSavedView;
  const { views, upsertView, removeView } = useSavedViews(
    builtInViews,
    dataset.company.id
  );

  const [bucketOverrides, setBucketOverrides] = useState<BucketOverrides>({});
  const [activeViewId, setActiveViewId] = useState(defaultView.id);
  const [filter, setFilterState] = useState<IPeriodFilter>(defaultView.filter);
  const [items, setItems] = useState<IReportItem[]>(defaultView.items);

  const setFilter = useCallback(
    (next: IPeriodFilter | ((previous: IPeriodFilter) => IPeriodFilter)) => {
      const resolved = typeof next === 'function' ? next(filter) : next;
      if (inlineOverride && resolved.anchor !== filter.anchor) {
        setItems((current) =>
          current.map((item) => ({
            ...item,
            override: InlineOverrideHelper.rebase(
              item.override,
              filter.anchor,
              resolved.anchor
            ),
          }))
        );
      }
      setFilterState(resolved);
    },
    [filter, inlineOverride]
  );

  // A newly closed month rebuilds the default view; only follow it when the
  // user is on that view, so a saved view keeps its chosen period.
  useEffect(() => {
    if (activeViewId === defaultView.id) setFilterState(defaultView.filter);
  }, [defaultView, activeViewId]);

  const table = useMemo(
    () => FactTableHelper.build({ dataset, currency, bucketOverrides }),
    [dataset, currency, bucketOverrides]
  );

  const activeView = useMemo(
    () => views.find((v) => v.id === activeViewId) ?? defaultView,
    [views, activeViewId, defaultView]
  );

  const isDirty = useMemo(
    () => !sameView(activeView, { filter, items }),
    [activeView, filter, items]
  );

  const setBucketOverride = useCallback(
    (lineId: string, bucket: ReportingBucket | null) => {
      setBucketOverrides((prev) => {
        const rest = Object.fromEntries(
          Object.entries(prev).filter(([id]) => id !== lineId)
        );
        return bucket ? { ...rest, [lineId]: bucket } : rest;
      });
    },
    []
  );

  const openView = useCallback(
    (id: string) => {
      const view = views.find((v) => v.id === id);
      if (!view) return;
      setActiveViewId(view.id);
      setFilterState(view.filter);
      setItems(view.items);
    },
    [views]
  );

  const saveView = useCallback(() => {
    if (activeView.isDefault) return;
    upsertView({
      ...activeView,
      filter,
      items,
      updatedAt: new Date().toISOString(),
    });
  }, [activeView, filter, items, upsertView]);

  const saveViewAs = useCallback(
    (name: string) => {
      const view: IReportSavedView = {
        id: SavedViewStorageHelper.newViewId(),
        name,
        filter,
        items,
        updatedAt: new Date().toISOString(),
      };
      upsertView(view);
      setActiveViewId(view.id);
    },
    [filter, items, upsertView]
  );

  const renameView = useCallback(
    (id: string, name: string) => {
      const view = views.find((v) => v.id === id);
      if (!view || view.isDefault) return;
      upsertView({ ...view, name, updatedAt: new Date().toISOString() });
    },
    [views, upsertView]
  );

  const deleteView = useCallback(
    (id: string) => {
      removeView(id);
      if (id === activeViewId) openView(defaultView.id);
    },
    [removeView, activeViewId, openView, defaultView.id]
  );

  const resetToDefault = useCallback(() => {
    setFilterState(defaultView.filter);
    setItems(defaultView.items);
  }, [defaultView]);

  return {
    dataset,
    currency,
    booksClosedThrough,
    columnLayout,
    inlineOverride,
    table,
    bucketOverrides,
    setBucketOverride,
    filter,
    setFilter,
    items,
    setItems,
    views,
    activeViewId,
    isDirty,
    openView,
    saveView,
    saveViewAs,
    renameView,
    deleteView,
    resetToDefault,
  };
};
